import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, FileText, LogOut, GraduationCap, Sun, Moon } from 'lucide-react';
import { useAuth, useTheme } from '../../App';
import NotificationBell from '../NotificationBell';

/**
 * Minimal sidebar layout for external (recruitment) agents.
 * Only two nav items: Dashboard + My Applications.
 */
const AgentLayout = ({ children }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const initial = (user?.name || user?.email || 'A').trim().slice(0, 1).toUpperCase();

  const navCls = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
      isActive
        ? 'bg-[#F5A623]/15 text-[#1A2A4F]'
        : 'text-gray-600 hover:bg-gray-50 hover:text-[#1A2A4F]'
    }`;

  return (
    <div className="min-h-screen flex bg-[#FAFAFB] dark:bg-slate-950">
      {/* Sidebar */}
      <aside
        className="w-64 bg-white dark:bg-slate-900 border-r border-gray-100 dark:border-slate-800 flex flex-col justify-between p-4 sticky top-0 h-screen"
        data-testid="agent-sidebar"
      >
        <div>
          {/* Brand */}
          <div className="flex items-center gap-2 px-2 py-3 mb-2">
            <div className="w-9 h-9 rounded-lg bg-[#1A2A4F] text-white flex items-center justify-center">
              <GraduationCap className="w-5 h-5" />
            </div>
            <div className="leading-tight">
              <div className="font-bold text-[#1A2A4F] text-sm">EduBridge Network</div>
              <div className="text-[10px] uppercase tracking-wider text-[#F5A623] font-bold">Agent Portal</div>
            </div>
          </div>

          {/* Agent badge */}
          <div className="flex items-center gap-3 px-2 py-3 bg-gradient-to-br from-[#F5A623]/10 to-transparent rounded-xl mb-4" data-testid="agent-profile-badge">
            <div className="w-10 h-10 rounded-full bg-[#F5A623] text-white flex items-center justify-center font-bold text-base">
              {initial}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-bold text-[#1A2A4F] truncate" data-testid="agent-name">{user?.name || 'Agent'}</div>
              <div className="text-[11px] text-gray-500 truncate" data-testid="agent-email">{user?.email}</div>
            </div>
          </div>

          {/* Nav */}
          <nav className="space-y-1">
            <NavLink to="/agent/home" className={navCls} data-testid="agent-nav-dashboard" end>
              <LayoutDashboard className="w-4 h-4" /> Dashboard
            </NavLink>
            <NavLink to="/agent/applications" className={navCls} data-testid="agent-nav-applications">
              <FileText className="w-4 h-4" /> My Applications
            </NavLink>
          </nav>
        </div>

        {/* Logout */}
        <button
          onClick={() => { logout(); navigate('/login', { replace: true }); }}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-rose-600 hover:bg-rose-50 transition-colors"
          data-testid="agent-logout-btn"
        >
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header
          className="h-14 bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-slate-800 px-6 flex items-center justify-end gap-3 sticky top-0 z-30"
          data-testid="agent-top-bar"
        >
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className="h-9 w-9 rounded-full inline-flex items-center justify-center hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-600 dark:text-slate-200 transition-colors"
            data-testid="agent-theme-toggle"
          >
            {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
          </button>
          <NotificationBell />
        </header>
        <div className="flex-1">{children}</div>
      </main>
    </div>
  );
};

export default AgentLayout;
